import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

// Icons, die direkt in PollPage verwendet werden
import { HiOutlinePlus } from "react-icons/hi";

// Import der Sub-Komponenten
import PollHeader from '../components/PollHeader'; // Pfad an deine Struktur anpassen
import ParticipantForm from '../components/ParticipantForm'; // Pfad an deine Struktur anpassen
import ParticipantList from '../components/ParticipantList'; // Pfad an deine Struktur anpassen

// Constants
const POLLS_API_BASE_URL = '/poll'; // Dein API-Endpunkt

// Hilfsfunktion zum Lesen eines bestimmten Cookies (nicht für httpOnly Cookies geeignet)
// Diese Funktion wird hier nicht mehr aktiv für die Autorisierungslogik verwendet,
// da httpOnly Cookies clientseitig nicht zuverlässig prüfbar sind.
// Sie kann für andere Zwecke (z.B. Debugging von nicht-httpOnly Cookies) im Code verbleiben.
const getCookie = (name) => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.startsWith(name + '=')) {
            return cookie.substring(name.length + 1);
        }
    }
    return null;
};

function PollPage(props) { // Props-Objekt empfangen
    const params = useParams();
    const idFromUrl = params.pollId; // ID aus URL-Parametern

    // Bestimme die tatsächlich zu verwendende pollId:
    // Priorisiere props.id (oder wie auch immer die Prop genannt wird, z.B. pollIdFromProp), 
    // falls vorhanden, ansonsten nimm die ID aus der URL.
    // Annahme: Wenn die ID als Prop übergeben wird, heißt sie `id`.
    const actualPollId = props.id || idFromUrl;

    const [poll, setPoll] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    
    const [showParticipantForm, setShowParticipantForm] = useState(false);
    const [verifyingParticipantId, setVerifyingParticipantId] = useState(null); 
    const [editingParticipantId, setEditingParticipantId] = useState(null);

    const fetchPollData = useCallback(async () => {
        if (!actualPollId) { 
            setPageError("Keine Umfrage-ID zum Laden vorhanden.");
            setPageLoading(false);
            setPoll(null); 
            return;
        }
        setPageLoading(true);
        setPageError(null); 
        try {
            const response = await axios.get(`${POLLS_API_BASE_URL}/${actualPollId}`, { withCredentials: true });
            setPoll(response.data);
        } catch (err) {
            console.error("Fehler beim Laden der Umfrage:", err);
            setPageError(err.response?.data?.message || "Umfrage konnte nicht geladen werden. Bitte versuche es später erneut.");
            setPoll(null); 
        } finally {
            setPageLoading(false);
        }
    }, [actualPollId]); 

    useEffect(() => {
        if (actualPollId) {
            fetchPollData();
        } else {
            setPageError("Keine Umfrage-ID spezifiziert.");
            setPageLoading(false);
            setPoll(null);
        }
    }, [actualPollId, fetchPollData]); 

    const handleShowParticipantForm = () => {
        setShowParticipantForm(true);
        setEditingParticipantId(null);
        setVerifyingParticipantId(null);
        setPageError(null); 
    };

    const handleCloseParticipantForm = () => {
        setShowParticipantForm(false);
    };

    const handleEditRequest = async (participant) => {
        setShowParticipantForm(false); 
        setPageError(null); 
        setPageLoading(true);

        try {
            await axios.post(`${POLLS_API_BASE_URL}/${actualPollId}/participants/${participant._id}/verify-email`, {}, { withCredentials: true });
            
            console.log(`Vorhandener Auth-Cookie für Teilnehmer ${participant._id} ist gültig. Wechsle direkt in den Bearbeitungsmodus.`);
            setEditingParticipantId(participant._id);
            setVerifyingParticipantId(null);
        } catch (err) {
            console.log(`Kein gültiger Auth-Cookie für Teilnehmer ${participant._id} gefunden oder Fehler (${err.response?.status}). Starte E-Mail-Verifizierungsprozess.`);
            setVerifyingParticipantId(participant._id); 
            setEditingParticipantId(null);
        } finally {
            setPageLoading(false);
        }
    };
    
    const handleCancelVerification = () => {
        setVerifyingParticipantId(null);
    };

    const handleConfirmVerification = async (participantIdToVerify, verificationEmailInput) => {
        setPageLoading(true); 
        setPageError(null);   
        try {
            await axios.post(`${POLLS_API_BASE_URL}/${actualPollId}/participants/${participantIdToVerify}/verify-email`, {
                email: verificationEmailInput
            }, { withCredentials: true });
            setEditingParticipantId(participantIdToVerify); 
            setVerifyingParticipantId(null); 
        } catch (err) {
            console.error("Fehler bei der E-Mail-Verifizierung (mit E-Mail-Eingabe):", err);
            throw err; 
        } finally {
            setPageLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingParticipantId(null);
    };

    const handleSaveEdit = async (participantIdToSave, editData) => {
        setPageLoading(true);
        setPageError(null);
        try {
            await axios.patch(`${POLLS_API_BASE_URL}/${actualPollId}/participants/${participantIdToSave}`, editData, { withCredentials: true });
            setEditingParticipantId(null); 
            await fetchPollData(); 
        } catch (err) {
            console.error("Fehler beim Speichern der Bearbeitung:", err);
            setPageLoading(false); 
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                setEditingParticipantId(null); 
                setVerifyingParticipantId(participantIdToSave); 
                throw new Error(err.response.data.message || "Bitte verifiziere deine E-Mail erneut, um die Änderungen zu speichern.");
            } else {
                throw new Error(err.response?.data?.message || 'Ein Fehler ist beim Speichern Ihrer Bearbeitung aufgetreten.');
            }
        }
    };

    const handleDeleteParticipant = async (participantIdToDelete) => {
        setPageLoading(true);
        setPageError(null);
        try {
            await axios.delete(`${POLLS_API_BASE_URL}/${actualPollId}/participants/${participantIdToDelete}`, { withCredentials: true });
            if (editingParticipantId === participantIdToDelete) setEditingParticipantId(null);
            if (verifyingParticipantId === participantIdToDelete) setVerifyingParticipantId(null);
            await fetchPollData(); 
        } catch (err) {
            console.error("Fehler beim Löschen der Teilnahme:", err);
            setPageLoading(false); 
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                setEditingParticipantId(null);
                setVerifyingParticipantId(participantIdToDelete);
                setPageError(err.response.data.message || "Bitte verifiziere deine E-Mail erneut, um den Eintrag zu löschen.");
            } else {
                setPageError(err.response?.data?.message || "Fehler beim Löschen der Teilnahme.");
            }
        }
    };

    if (pageLoading && !poll && actualPollId) { 
        return <div className='rakete-font fs-7 ms-3 text-secondary ps-2 py-2 text-center'>Umfrage wird geladen...</div>;
    }
    if (pageError) { 
        return <div className='alert alert-danger text-center rakete-font container my-4' style={{ maxWidth: '600px' }}>{pageError}</div>;
    }
    if (!poll && !pageLoading && actualPollId) { 
        return <div className='rakete-font fs-7 ms-3 text-secondary ps-2 py-2 text-center'>Keine Umfragedaten gefunden oder Umfrage existiert nicht.</div>;
    }
    if (!actualPollId && !pageLoading){ 
         return <div className='alert alert-warning text-center rakete-font container my-4' style={{ maxWidth: '600px' }}>Keine Umfrage-ID spezifiziert.</div>;
    }

    if (!poll) {
        return <div className='rakete-font fs-7 ms-3 text-secondary ps-2 py-2 text-center'>Lade Umfragedaten oder keine ID vorhanden...</div>;
    }

    const totalPeopleCount = poll.participants ? poll.participants.reduce((sum, p) => sum + 1 + (p.guests || 0), 0) : 0;

    return (
        <>
            <div className="container py-3">
                <div className="rounded-4 bg-white py-3 shadow-lg my-3 p-lg-4 p-3 mx-auto" style={{ maxWidth: '700px' }}>
                    <PollHeader title={poll.title} description={poll.description} />

                    {pageError && <div className="alert alert-danger fs-8 rakete-font p-2 mb-3">{pageError}</div>}
                    
                    <div className='row'> 
                        <div className='col text-start rakete-font fs-6 text-uppercase fw-semibold'>Teilnehmer*innen</div>
                        <div className='col text-end rakete-rot rakete-font fw-semibold pe-4'>{totalPeopleCount}</div>
                    </div>
                    
                    <ParticipantList
                        participants={poll.participants || []} 
                        editingParticipantId={editingParticipantId}
                        verifyingParticipantId={verifyingParticipantId}
                        onEditRequest={handleEditRequest}
                        onCancelVerification={handleCancelVerification}
                        onConfirmVerification={handleConfirmVerification}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onDeleteParticipant={handleDeleteParticipant}
                        globalLoading={pageLoading} 
                    />

                    {!showParticipantForm && !editingParticipantId && !verifyingParticipantId && (
                        <div className="text-center mt-4">
                            <button
                                className="btn btn-danger rakete-font shadow-sm d-flex align-items-center justify-content-center mx-auto"
                                onClick={handleShowParticipantForm}
                                disabled={pageLoading || !actualPollId} // Deaktiviere Button auch, wenn keine ID vorhanden ist
                                style={{ padding: '0.5rem 1rem' }}
                            >
                                <HiOutlinePlus size={22} className="me-2" />
                                Teilnehmen
                            </button>
                        </div>
                    )}

                    {showParticipantForm && actualPollId && ( // Zeige Formular nur, wenn eine ID vorhanden ist
                        <ParticipantForm
                            pollId={actualPollId} 
                            onClose={handleCloseParticipantForm}
                            fetchPollData={fetchPollData}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

export default PollPage;
